import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useRef } from 'react';
import { addressDesc } from '../../pages/Form/forms/constants';
import { getDefaultValue, validatePincodeByCountry } from '../../pages/Form/components/PincodeField/helper';
import pincodeService from '../../pages/Form/components/PincodeField/PincodeService';
import PincodeField from '../../pages/Form/components/PincodeField/PincodeField';
import CountrySelectService from '../CSC/CountrySelect/CountrySelectService';
import { AddressAutocompleter } from '../InputField/AddressAutocompleter';
import { InputField } from '../InputField/InputField';
import { SelectField } from '../SelectField/SelectField';
import { useTranslation } from '../../store/context/TranslationContext';

export const validationConfig = {
    street: {
        regex: /^[a-zA-Z0-9#]+[,. # a-zA-Z0-9/-]*$/,
        maxLength: 40,
        minLength: 1,
        error: 'Please enter a valid Street name',
    },
    buildingNumber: {
        regex: /^[a-zA-Z0-9#]+[,. # a-zA-Z0-9/-]*$/,
        maxLength: 40,
        minLength: 1,
        error: 'Please enter a valid Building and Number',
    },
    city: {
        regex: /^[a-zA-Z\s.,'&-]{1,50}$/,
        maxLength: 50,
        minLength: 1,
        error: 'Please enter a valid city name.',
    },
    state: {
        regex: /^[a-zA-Z\s.,'-]{1,50}$/,
        maxLength: 50,
        minLength: 1,
        error: 'Please enter a valid state',
    },
    pinCode: {
        regex: /^[a-zA-Z0-9\- ]{3,10}$/,
        maxLength: 10,
        error: 'Please enter a valid postal/zip code.',
    },
    country: {
        required: true,
        error: 'Please select a valid Country.',
    },
};

export const validateAddress = (address = {}) => {
    const errors = {};
    Object.keys(validationConfig).forEach((field) => {
        // A country with a fixed postal code (e.g. HK -> 999077) hides the Zip field; its value is
        // the country default the user cannot edit - never gate validation on it.
        if (field === 'pinCode' && address.hidePinCode) return;
        const value = address[field] || '';
        const cfg = validationConfig[field];
        let err = '';

        if (cfg.required && !value.trim()) err = cfg.error;
        else if (cfg.regex && !cfg.regex.test(value)) err = cfg.error;
        else if (value.length > cfg.maxLength) err = cfg.error;
        else if (cfg.minLength && value.length < cfg.minLength) err = cfg.error;
        else if (field === 'pinCode' && !validatePincodeByCountry(address.country, value)) err = cfg.error;

        if (err) errors[field] = err;
    });
    return errors;
};

export const isAddressValid = (addr) => Object.keys(validateAddress(addr)).length === 0;

const addressLabels = {
    street: 'Street Name',
    buildingNumber: 'Building and Number',
    city: 'City',
    state: 'State / Province',
    pinCode: 'Zip Code',
    country: 'Country',
};


const AddressForm = observer(
    ({ address = {}, onChange, showErrors = false, disabled = false, className = '' }) => {
        const { t } = useTranslation();

        const formData = useMemo(() => _.cloneDeep(address), [JSON.stringify(address)]);

        // Latest form state for the async country handler below: it awaits the postal-code config
        // fetch, and a keystroke landing during that await would be clobbered if the payload were
        // built from the render-time closure. The ref always points at the current render's data.
        const formDataRef = useRef(formData);
        formDataRef.current = formData;

        const handleFieldChange = async (field, value) => {
            if (field !== 'country') {
                onChange({ ...formDataRef.current, [field]: value });
                return;
            }
            // Ensure the postal-code config is loaded BEFORE reading the country's default pincode.
            // The prefetch is fire-and-forget, so on a fast country change records could still be
            // empty and getDefaultValue would wrongly return null (e.g. Hong Kong default 999077 not
            // auto-populating). fetch() is cached, so this is a no-op once records are loaded.
            try {
                await pincodeService.fetch();
            } catch (e) {
                // Non-blocking: fall through with whatever config is cached.
            }
            const defaultPin = getDefaultValue(value);
            // Compose from the ref AFTER the await so edits made while the fetch was in flight survive.
            onChange({
                ...formDataRef.current,
                country: value,
                hidePinCode: !!defaultPin,
                pinCode: defaultPin || '',
            });
        };

        const valErr = (field) =>
            showErrors ? t(validateAddress(formData)[field] || '') : '';

        return (
            <div className={`row ${className}`}>
                <AddressAutocompleter
                    placeholder={t(addressLabels.street)}
                    value={formData.street}
                    onChange={(v) => handleFieldChange('street', v)}
                    onSelect={async (components) => {
                        if (components.country) {
                            // Cached fetch = no-op once loaded; guards the records-not-yet-loaded race.
                            try {
                                await pincodeService.fetch();
                            } catch (e) {
                                // Non-blocking: fall through with whatever config is cached.
                            }
                        }

                        // Compose from the ref AFTER the await so edits made while the fetch was
                        // in flight survive (same stale-clone guard as handleFieldChange).
                        const merged = { ...formDataRef.current, ...components };

                        if (components.country) {
                            const defaultPin = getDefaultValue(components.country);
                            merged.hidePinCode = !!defaultPin;
                            /* If autocomplete didn’t supply pinCode, fall back to default */
                            merged.pinCode = components.pinCode ?? merged.pinCode ?? defaultPin ?? '';
                        } else {
                            // Resolved place carries no country (components initializes it to "").
                            // Keep the user's selected country + pincode pair instead of blanking
                            // them, which made the country field flicker/toggle.
                            merged.country = formDataRef.current.country;
                            merged.pinCode = formDataRef.current.pinCode;
                            merged.hidePinCode = formDataRef.current.hidePinCode;
                        }

                        onChange(merged);   //  🔹 single pass-back
                    }}

                    error={valErr('street')}
                    showErr={showErrors}
                    desc={t(addressDesc.street)}
                    isDisabled={disabled}
                    className="col-12"
                />

                {['buildingNumber'].map((f) => (
                    <InputField
                        key={f}
                        placeholder={t(addressLabels[f])}
                        value={formData[f]}
                        onChange={(v) => handleFieldChange(f, v)}
                        error={valErr(f)}
                        showErr={showErrors}
                        type="text"
                        maxLength={validationConfig[f].maxLength}
                        disabled={disabled}
                        desc={t(addressDesc[f])}
                        className="col-12"
                    />
                ))}

                {['city', 'state'].map((f) => (
                    <InputField
                        key={f}
                        placeholder={t(addressLabels[f])}
                        value={formData[f]}
                        onChange={(v) => handleFieldChange(f, v)}
                        error={valErr(f)}
                        showErr={showErrors}
                        type="text"
                        regex={/^([a-zA-Z\s\-']*)$/}
                        maxLength={validationConfig[f].maxLength}
                        disabled={disabled}
                        desc={t(addressDesc[f])}
                        className="col-12 col-sm-6"
                    />
                ))}

                {!formData.hidePinCode && (
                    <PincodeField
                        key="pinCode"
                        countryCode={formData.country}
                        placeholder={t(addressLabels.pinCode)}
                        value={formData.pinCode}
                        onChange={(v) => handleFieldChange('pinCode', v)}
                        error={valErr('pinCode')}
                        showErr={showErrors}
                        maxLength={10}
                        className="col-12 col-sm-6 me-0 pe-0"
                        disabled={disabled}
                    />
                )}

                <SelectField
                    value={formData.country}
                    error={valErr('country')}
                    showErr={showErrors}
                    data={CountrySelectService.cyclosCountryMaster}
                    placeholder={t('Country')}
                    label={t('Country')}
                    noLabel
                    isDisabled={disabled}
                    desc={addressDesc.country}
                    className="col-12 col-sm-6 with-margin-top"
                    onChange={(v) => handleFieldChange('country', v?.value)}
                />

                {formData.hidePinCode && (
                    <div className="col-12 col-sm-6 me-0 pe-0" aria-hidden="true" />
                )}
            </div>
        );
    }
);

export default AddressForm;
